/* eslint react-hooks/exhaustive-deps: "warn" */

import { useContext } from 'react';

import { ContentContext } from 'context/ContentContext';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { HeroSection } from 'ui/molecules/HeroSection/HeroSection';
import { CompanyVerificationCard } from 'ui/molecules/CompanyVerificationCard/CompanyVerificationCard';
import { EditorialStepsSection } from 'ui/molecules/EditorialStepsSection/EditorialStepsSection';
import { StatsBlock } from 'ui/molecules/StatsBlock/StatsBlock';
import { OurCustomersV2 } from 'ui/molecules/OurCustomers/OurCustomersV2';
import { NumberedFeatureCards } from 'ui/organisms/NumberedFeatureCards/NumberedFeatureCards';
import { NumberedStatsSection } from 'ui/organisms/NumberedStatsSection/NumberedStatsSection';

import { CaseStudyCard } from 'landing-pages/shared/CaseStudyCard/CaseStudyCard';

import styles from './SupplyChainThirdPartyRiskPage.module.scss';

import { ConcentrationSection } from './sections/ConcentrationSection/ConcentrationSection';
import { DeliverySection } from './sections/DeliverySection/DeliverySection';
import { MonitoringSection } from './sections/MonitoringSection/MonitoringSection';
import { ProvenanceSection } from './sections/ProvenanceSection/ProvenanceSection';
import { TeamUseCasesSection } from './sections/TeamUseCasesSection/TeamUseCasesSection';
import {
  getDifferentiatorRows,
  getProblemItems,
  getProvenanceItems,
  getSolutionItems,
  getTeamUseCases,
} from './supplyChainThirdPartyRisk.data';

export const SupplyChainThirdPartyRiskPage = () => {
  const { secondaryNavigation } = useContext(ContentContext);
  const { t } = useInt();

  const solutionItems = getSolutionItems(t);
  const provenanceItems = getProvenanceItems(t);
  const teamUseCases = getTeamUseCases(t);
  const problemItems = getProblemItems(t);

  return (
    <div className={styles.wrapper}>
      <HeroSection
        align="left"
        mediaPosition="right"
        className={styles.hero}
        title={t('Vet every supplier against its home registry.')}
        description={t(
          "Onboard, screen, and monitor third parties in 200+ countries — with primary, government-sourced data and a source on every field. Know who you're really doing business with before you sign, and the moment anything changes after.",
        )}
        actions={
          <>
            {secondaryNavigation?.free_trial?.slug && (
              <Button type="dark" size="xxl" route={secondaryNavigation.free_trial.slug}>
                {t('Free Trial')}
              </Button>
            )}

            {secondaryNavigation?.book_demo?.slug && (
              <Button type="dark-outline" size="xxl" route={secondaryNavigation.book_demo.slug}>
                {t('See what we check')}
              </Button>
            )}
          </>
        }
        media={<CompanyVerificationCard />}
      />

      <OurCustomersV2 hasBackground={false} />

      <StatsBlock
        theme="dark"
        hasPadding={false}
        features={[
          { value: '400', suffix: '+', title: t('Government registries, direct') },
          { value: '200', suffix: '+', title: t('Countries & territories') },
          { value: '600', suffix: 'M+', title: t('Company profiles') },
          { value: t('Daily'), title: t('Change monitoring') },
        ]}
      />

      <EditorialStepsSection
        title={t("Third-party risk hides where a vendor form can't reach")}
        description={t(
          "A supplier clears onboarding on the strength of a self-reported form. The real exposure sits in what the form never asked — and in what changes after you've signed.",
        )}
        steps={problemItems}
      />

      <NumberedFeatureCards
        theme="dark"
        columns={3}
        title={t('One verified source of supplier truth, across 200+ countries')}
        description={t(
          'Global Database resolves every third party to its official record — identity, ownership, sanctions exposure, and financial health — sourced directly from government registries, with the provenance to put in front of an auditor.',
        )}
        items={solutionItems}
      />

      <NumberedStatsSection title={t('The difference is the source')} rows={getDifferentiatorRows(t)} />

      <CaseStudyCard
        company="WNS"
        title={t('WNS Accelerates Global Supplier Due Diligence')}
        summary={t(
          'WNS runs instant verification across a vendor network spanning 200+ countries — replacing what was a slow, jurisdiction-by-jurisdiction due diligence process with one consistent check.',
        )}
        href="/wns-accelerates-global-supplier-due-diligence-with-instant-verification-across-200-countries"
      />

      <ConcentrationSection />

      <MonitoringSection ctaRoute={secondaryNavigation?.book_demo?.slug} />

      <ProvenanceSection
        title={t('Provenance you can put in front of an auditor')}
        description={t(
          'Supply chain due diligence rules increasingly ask you to know — and evidence — who your suppliers are and who owns them. Every field we return carries its registry, filing link, and retrieval timestamp.',
        )}
        items={provenanceItems}
        disclaimer={t(
          'Global Database provides primary-source data to support your due diligence. It does not certify compliance — your programme and counsel determine what each regime requires.',
        )}
      />

      <TeamUseCasesSection title={t('Built for the teams who own third-party risk')} items={teamUseCases} />

      <DeliverySection />
    </div>
  );
};
